
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import BridgePasswordModal from './components/BridgePasswordModal';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import VitalsPage from './pages/VitalsPage';
import HealthProfilePage from './pages/HealthProfilePage';
import CommunityPage from './pages/CommunityPage';
import ImportPage from './pages/ImportPage';
import MedicationsPage from './pages/MedicationsPage';
import ReportPage from './pages/ReportPage';
import ConnectPage from './pages/ConnectPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, authLoading } = useAuth();
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-400">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }
  if (!currentUser) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  const {
    currentUser,
    authLoading,
    bridgeNeedsPassword,
    bridgeTokenInfo,
    bridgeProcessing,
    bridgeError,
    completeBridgeLogin,
    cancelBridgeLogin,
  } = useAuth();

  // Block routing while bridge token is being processed
  const hasBridgeToken = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('talkoru_token');
  const waitingForBridge = hasBridgeToken && !bridgeNeedsPassword && !bridgeError;

  if (authLoading || waitingForBridge) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-400">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          <span className="text-sm">{waitingForBridge ? 'Processing login...' : 'Initializing...'}</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      {bridgeNeedsPassword && bridgeTokenInfo && (
        <BridgePasswordModal
          tokenInfo={bridgeTokenInfo}
          isProcessing={bridgeProcessing}
          error={bridgeError}
          onSubmit={completeBridgeLogin}
          onCancel={cancelBridgeLogin}
        />
      )}

      <Routes>
        <Route path="/" element={currentUser ? <Navigate to="/app/dashboard" replace /> : <LandingPage />} />
        <Route path="/login" element={currentUser ? <Navigate to="/app/dashboard" replace /> : <LoginPage />} />

        <Route path="/app/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/app/vitals" element={<ProtectedRoute><VitalsPage /></ProtectedRoute>} />
        <Route path="/app/profile" element={<ProtectedRoute><HealthProfilePage /></ProtectedRoute>} />
        <Route path="/app/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
        <Route path="/app/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
        <Route path="/app/medications" element={<ProtectedRoute><MedicationsPage /></ProtectedRoute>} />
        <Route path="/app/report" element={<ProtectedRoute><ReportPage /></ProtectedRoute>} />
        <Route path="/app/connect" element={<ProtectedRoute><ConnectPage /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
